import React, {useEffect, useMemo, useState} from "react";
import { withTranslation } from "react-i18next";
import {connect, useSelector} from "react-redux";
import Modal from "react-modal";
import classNames from "classnames";
import "./index.scss";

import {getControlCenters} from "../../../services/restaurant";
import { DATECONTROL_TYPE, getOrderIntegrationTypeFilterItems, WAITER_TYPES, ORDER_INTEGRATION_TYPE_FILTER, ReportType } from "../../../utils/enums";
import {
  getISOTime,
  getWeekPeriod, prepareMonth,
  preparePreviousMonth,
  prepareToday,
  prepareYesterday
} from "../../../utils/convertTime";

import { ModalPopup } from "../ModalPopup/ModalPopup";
import Dropdown from "../Dropdown";
import OrderDatetimeControl from "../OrderDatetimeControl";
import Waiter from "../Waiter/Waiter";
import moment from "moment";
import DropdownMultiSelect from "../DropdownMultiSelect";

Modal.setAppElement("#root");

const SaveReportPopup = ({ t, restaurant, isOpen, integrationTypeFilter, onClose, onSave }) => {
  const territoriesItems = useSelector(state => state.restaurant.data.territories);
  const getIniDate = () => {
    const start = new Date();
    start.setDate(start.getDate() - 1);
    return start.toString();
  };
  const initDate = getIniDate();

  const depots = restaurant.depots || [];
  const depotsAsContolCenter = useMemo(() => depots.filter(d => d.depotAsControlCenter).map(d => d.id), [depots]);
  const defaultControlCenters = getControlCenters().filter(cc => !depotsAsContolCenter.includes(cc.controlCenterId));
  const isCustomType = integrationTypeFilter || integrationTypeFilter === 0;
  const [depot, setDepot] = useState([]);
  const [controlCenters, setControlCenters] = useState([]);
  const [startDate, setStartDate] = useState(getIniDate());
  const [endDate, setEndDate] = useState(new Date().toString());
  const [filePerDepot, setFilePerDepot] = useState(false);
  const [recalculatePriceForBusiness, setRecalculatePriceForBusiness] = useState(false);
  const [territory, setTerritory] = useState(null);
  const [orderIntegrationTypeFilter, setOrderIntegrationTypeFilter] = useState(getOrderIntegrationTypeFilterItems()[1]);
  const [basedOnTargetDate, setBasedOnTargetDate] = useState(false);
  const [isCustomTime, setIsCustomTime] = useState(false);

  const handleOnChangeFilterType = (filter) => {
    if([ORDER_INTEGRATION_TYPE_FILTER.allMarketplaceOrders, ORDER_INTEGRATION_TYPE_FILTER.application].includes(filter.key)) {
      setControlCenters([defaultControlCenters[0]?.controlCenterId]);
    }
    setOrderIntegrationTypeFilter(filter);
  }

  const checkDiffTime = (time) => {
    return moment(initDate).get('minutes') !== moment(time).get('minutes') || moment(initDate).get('hours') !== moment(time).get('hours');
  }

  useEffect(() => {
    setIsCustomTime(checkDiffTime(startDate) || checkDiffTime(endDate));
    // eslint-disable-next-line
  }, [startDate, endDate]);
  

  const handleOnSaveReport = () => {
    return new Promise((resolve, reject) => {
      onClose();
      if (filePerDepot && depot.id === null) {
        onSave(getCsvForm(
          resolve, 
          reject, 
          getISOTime(startDate), 
          getISOTime(endDate),
          null,
          filePerDepot,
          controlCenters,
          territory && territory.key,
          recalculatePriceForBusiness,
          basedOnTargetDate,
          isCustomTime,
          restaurant.depots.map(d => d.id)
        ));
      } else {
        onSave(getCsvForm(
          resolve, 
          reject, 
          getISOTime(startDate), 
          getISOTime(endDate),
          depot,
          filePerDepot,
          controlCenters,
          territory && territory.key,
          recalculatePriceForBusiness,
          basedOnTargetDate,
          isCustomTime
        ));
      }
    });
  };

  const getCsvForm = (resolve, reject, startDate, endDate, id, filePerDepot, controlCenterIds, territoryId, recalculatePriceForBusiness, basedOnTargetDate, isCustomTime, depotIds) => {
    let includeMarketPlaceOrders = false;
    let includePartnerOrders = false;
    let reportType = null;

    switch (isCustomType ? integrationTypeFilter : (orderIntegrationTypeFilter && orderIntegrationTypeFilter.key)) {
      case ORDER_INTEGRATION_TYPE_FILTER.external:
        reportType = ReportType.Orders;
        break;
      case ORDER_INTEGRATION_TYPE_FILTER.allOrders:
        includeMarketPlaceOrders = true;
        reportType = ReportType.Orders;
        break;
      case ORDER_INTEGRATION_TYPE_FILTER.application:
        reportType = ReportType.AccountOrders;
        break;
        case ORDER_INTEGRATION_TYPE_FILTER.allMarketplaceOrders:
        includePartnerOrders = true;
        reportType = ReportType.AccountOrders;
        break;
      default:
        break;
    }

    return {
      start: startDate,
      end: endDate, 
      depotIds: id || depotIds,
      reportType,
      controlCenterIds,
      customerVersion: filePerDepot,
      includeMarketplace: includeMarketPlaceOrders,
      territoryId,
      recalculatePriceForBusiness,
      includePartnerOrders,
      basedOnTargetDate,
      isCustomTime,
    }
  }

  const setTodayDate = () => {
    const {from, to} = prepareToday();
    setStartDate(from);
    setEndDate(to);
  };

  const setYesterdayDate = () => {
    const {from, to} = prepareYesterday();
    setStartDate(from);
    setEndDate(to);
  };

  const setWeekDate = () => {
    const { from, to } = getWeekPeriod();
    setStartDate(from);
    setEndDate(to);
  };

  const setMonthDate = () => {
    const {from, to} = prepareMonth();
    setStartDate(from);
    setEndDate(to);
  };

  const setPreviousMonthDate = () => {
    const {from, to} = preparePreviousMonth();
    setStartDate(from);
    setEndDate(to);
  };

  return (
    <ModalPopup
      width={"420px"}
      height={"385px"}
      isOpen={isOpen}
      onClose={onClose}
      content={
        <div className={classNames("popupContainer")}>
          <div className={classNames("saveReportContainer")}>
            <div className={classNames("containerHeader")}>
              <div className={classNames("title")}>
                {t("dialogs.saveReport.SAVE_REPORT_LABEL")}
              </div>
              <div className={classNames("link")} onClick={onClose}>
                {t("basic.CLOSE_BUTTON")}
              </div>
            </div>
            <div className={classNames("containerContent")}>             
              <div className={classNames("item")}>
                <div className="links">
                  <div className="link" onClick={setTodayDate}>
                    {t("shifts.TODAY_LINK")}
                  </div>
                  <div className="link" onClick={setYesterdayDate}>
                    {t("shifts.YESTERDAY_LINK")}
                  </div>
                  <div className="link" onClick={setWeekDate}>
                    {t("shifts.WEEK_LINK")}
                  </div>
                  <div className="link" onClick={setMonthDate}>
                    {t("shifts.MONTH_LINK")}
                  </div>
                  <div className="link" onClick={setPreviousMonthDate}>
                    {t("shifts.PREVIOUS_MONTH_LINK")}
                  </div>
                </div>
              </div>
              {!isCustomType && (
                <div className={classNames("item")}>
                  <div className={classNames("leftSide")}>
                    {t("dashboard.orders.ORDER_INTEGRATION_TYPE_FILTER_LABEL")}
                  </div>
                  <div className={classNames("centerSide")}>
                    <Dropdown values={getOrderIntegrationTypeFilterItems()} 
                      value={(orderIntegrationTypeFilter && orderIntegrationTypeFilter.value) || ""}
                      onSelect={val => handleOnChangeFilterType(val)}
                    />
                  </div>
                </div>
              )}
              {!isCustomType && territoriesItems && !!territoriesItems.length && (
                <div className={classNames("item")}>
                  <div className={classNames("leftSide")}>
                    {t("dashboard.orders.ORDER_DELIVERY_TERRITORY_LABEL")}
                  </div>
                  <div className={classNames("centerSide")}>
                    <Dropdown values={[{key: null, value: "-"}].concat(territoriesItems.map(t => ({key: t.id, value: t.name})))}
                      value={(territory && territory.value) || "-"}
                      onSelect={setTerritory}
                    />
                  </div>
                </div>
              )}
              {!isCustomType && (
                <div className={classNames("item")}>
                  <div className={classNames("leftSide")}>
                    {t("dialogs.saveReport.DEPOT_LABEL")}
                  </div>
                  <div className={classNames("centerSide")}>
                    <DropdownMultiSelect
                      placeholderEmpty={t('admin-orders.ADMIN_ORDERS_SEARCH_FILTER_ALL')}
                      placeholderMulti={t('dialogs.saveReport.DEPOTS_LABEL')}
                      onApply={setDepot}
                      values={(depots).map(depot => ({value: depot.id, key: depot.name}))}
                      selectedValues={depot}
                    />
                  </div>
                </div>
              )} 
              {!isCustomType && (
                <div className={classNames("item")}>
                  <div className={classNames("leftSide")}>
                    {t("dialogs.saveReport.CONTROL_CENTER")}
                  </div>
                  <div className={classNames("centerSide")}>
                    <DropdownMultiSelect
                      placeholderEmpty={t('admin-orders.ADMIN_ORDERS_SEARCH_FILTER_ALL')}
                      placeholderMulti={t('controlCenters.SELECT_PLACEHOLDER')}
                      onApply={setControlCenters}
                      values={(defaultControlCenters).map(cc => ({value: cc.controlCenterId, key: cc.name}))}
                      selectedValues={controlCenters}
                    />
                  </div>
                </div>
              )}              
              <div className={classNames("item")}>
                <div className={classNames("centerSide")}>
                  <OrderDatetimeControl
                    tab={1}
                    time={new Date(startDate)}
                    type={DATECONTROL_TYPE.datetime}
                    isEditable={true}
                    hideIcon={true}
                    label={t("dialogs.saveReport.START_DATE_LABEL")}
                    onSelect={date => setStartDate(date)}
                  />
                </div>
              </div>
              <div className={classNames("item")}>
                <div className={classNames("centerSide")}>
                  <OrderDatetimeControl
                    tab={1}
                    time={new Date(endDate)}
                    type={DATECONTROL_TYPE.datetime}
                    isEditable={true}
                    hideIcon={true}
                    label={t("dialogs.saveReport.END_DATE_LABEL")}
                    onSelect={date => setEndDate(date)}
                  />
                </div>
              </div>
              {!isCustomType && (
                <div className={classNames("item itemCheckbox")}>
                  <div className={classNames("leftSide")}>
                    {t("dialogs.saveReport.FILE_PER_DEPOT")}
                  </div>
                  <div className={classNames("centerSide")}>
                    <div className={classNames("filePerDepot")}>
                      <input type="checkbox" value={filePerDepot} onChange={() => setFilePerDepot(!filePerDepot)} />
                    </div>
                  </div>
                </div>
              )}
              {!isCustomType && (
                  <div className={classNames("item itemCheckbox")}>
                    <div className={classNames("leftSide")}>
                      {t("dialogs.saveReport.RECALCULATED_PRICE")}
                    </div>
                    <div className={classNames("centerSide")}>
                      <div className={classNames("filePerDepot")}>
                        <input type="checkbox" value={recalculatePriceForBusiness} onChange={() => setRecalculatePriceForBusiness(!recalculatePriceForBusiness)} />
                      </div>
                    </div>
                  </div>
              )}
               {!isCustomType && (
                  <div className={classNames("item itemCheckbox")}>
                    <div className={classNames("leftSide")}>
                      {t("dialogs.saveReport.BASED_ON_DELIVERY_DATE")}
                    </div>
                    <div className={classNames("centerSide")}>
                      <div className={classNames("filePerDepot")}>
                        <input type="checkbox" value={basedOnTargetDate} onChange={() => setBasedOnTargetDate(!basedOnTargetDate)} />
                      </div>
                    </div>
                  </div>
              )}
            </div>
            <div className={classNames("containerControls")}>
              <Waiter
                useClass={classNames("btn", "confirm")}
                type={WAITER_TYPES.button}
                handleOnClick={handleOnSaveReport}
                stopPropagation={true}
                spanText={t("basic.OK_BUTTON")}
              />
              <button
                className={classNames("btn", "denied", "btnCancel")}
                onClick={onClose}
              >
                {t("basic.CANCEL_BUTTON")}
              </button>
            </div>
          </div>
        </div>
      }
    />
  );
};

const mapStateToProps = state => ({
  restaurant: state.restaurant.data
});

export default connect(mapStateToProps)(withTranslation()(SaveReportPopup));
